import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getMapboxToken } from '../../services/mapService';
import mapPreloader from '../../utils/mapPreloader';
import FullscreenMapButton from '../../screens/UserProfile/components/FullscreenMapButton';
import './MapDisplay.css';
import BottomDockedSheet from '../common/BottomDockedSheet';

/**
 * MapDisplay component for showing a read-only map with a marker
 * @param {Object} props
 * @param {Object} props.location - Location object with lat, lng, name
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.zoom - Initial zoom level (default: 12)
 * @param {boolean} [props.bottomSheetEnabled]
 * @param {boolean} [props.bottomSheetExpanded]
 * @param {Function} [props.onBottomSheetToggle]
 * @param {Function} [props.onBottomSheetCollapse]
 * @param {number} [props.bottomSheetCollapsedHeight]
 * @param {number} [props.bottomSheetExpandedHeight]
 * @param {string} [props.bottomSheetTitle]
 * @param {string} [props.bottomSheetSubtitle]
 * @param {Function} [props.onBottomSheetPrimary]
 * @param {string} [props.bottomSheetPrimaryLabel]
 * @param {React.ReactNode} [props.bottomSheetContent]
 * @param {{ origin: {lat:number,lng:number}, destination: {lat:number,lng:number} } | null} [props.directions]
 * @param {('driving'|'walking'|'cycling')} [props.directionsProfile]
 * @param {(info: { distanceMeters: number, durationSeconds: number } | null) => void} [props.onDirectionsInfo]
 */
const MapDisplay = ({ 
  location, 
  className = '', 
  zoom = 12,
  bottomSheetEnabled = false,
  bottomSheetExpanded = false,
  onBottomSheetToggle,
  onBottomSheetCollapse,
  bottomSheetCollapsedHeight = 80,
  bottomSheetExpandedHeight = 300,
  bottomSheetTitle,
  bottomSheetSubtitle,
  onBottomSheetPrimary,
  bottomSheetPrimaryLabel = 'Directions',
  bottomSheetContent,
  directions = null,
  directionsProfile = 'driving',
  onDirectionsInfo
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null); // Stores the mapboxgl.Map instance
  const markerRef = useRef(null); // Stores the mapboxgl.Marker instance
  const [mapError, setMapError] = useState(false);
  const lastPaddingRef = useRef({ top: 0, right: 0, bottom: 0, left: 0 });
  const routeSourceIdRef = useRef('post-directions-source');
  const routeLayerIdRef = useRef('post-directions-layer');
  const originMarkerRef = useRef(null);
  const destMarkerRef = useRef(null);
  const lastRouteBoundsRef = useRef(null);

  // Effect for map initialization and unmount cleanup
  useEffect(() => {
    if (!mapContainer.current) return; // Should not happen if div is rendered

    const token = getMapboxToken();
    if (!token) {
      console.error('Mapbox token not found - maps will be disabled');
      setMapError(true);
      return;
    }

    try {
      mapboxgl.accessToken = token;
    } catch (error) {
      console.error('Error setting Mapbox token:', error);
      setMapError(true);
      return;
    }

    // Use initial props for center and zoom, with fallbacks
    const initialCenter = (location && typeof location.lng === 'number' && typeof location.lat === 'number')
                          ? [location.lng, location.lat]
                          : [0, 0]; // Default center if location is initially invalid
    
    const initialZoom = (location && typeof location.lng === 'number' && typeof location.lat === 'number')
                        ? zoom
                        : 3; // Default zoom

    const mapLoadStartTime = Date.now();
    
    // Optimized map configuration for faster loading
    let mapInstance;
    try {
      mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: initialCenter,
        zoom: initialZoom,
        trackResize: true,
        // Performance optimizations
        preserveDrawingBuffer: false,
        antialias: false,
        attributionControl: false,
        // Faster loading
        maxZoom: 18,
        minZoom: 1,
        // Disable unnecessary features
        cooperativeGestures: false,
        // Optimize for mobile
        maxPitch: 60,
        maxBounds: null,
      });
    } catch (error) {
      console.error('Error creating Mapbox map:', error);
      setMapError(true);
      return;
    }

    // Track map load performance
    mapInstance.on('load', () => {
      const loadTime = Date.now() - mapLoadStartTime;
      console.log(`🗺️ Map loaded in ${loadTime}ms`);
    });

    // Add error handling
    mapInstance.on('error', (e) => {
      console.error('Mapbox map error:', e);
    });

    // Add navigation control only if needed
    try {
      mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
    } catch (error) {
      console.error('Error adding navigation control:', error);
    }
    map.current = mapInstance;

    // Cleanup function for unmount
    return () => {
      if (markerRef.current) {
          markerRef.current.remove();
          markerRef.current = null;
      }
      if (map.current) { // map.current here refers to the one set in this effect's closure
        map.current.remove(); // This should clear all map-internal listeners too
        map.current = null;
      }
    };
  }, []); // Empty dependency array: runs once on mount, cleans up on unmount

  // Effect for handling location and zoom updates
  useEffect(() => {
    // If map isn't initialized yet, wait for the mount effect.
    if (!map.current) {
      return;
    }

    // If location becomes invalid, remove the marker.
    if (!location || typeof location.lat !== 'number' || typeof location.lng !== 'number') {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      // Optionally, you could fly to a default view:
      // map.current.flyTo({ center: [0, 0], zoom: 2 });
      return;
    }

    // Define the logic to update map view and marker
    const updateMapViewAndMarker = () => {
      if (!map.current) return; // Guard: map might have been removed

      map.current.flyTo({ 
        center: [location.lng, location.lat], 
        zoom: zoom,
        speed: 1.2 // Adjust speed as desired
      });

      // Remove old marker if it exists
      if (markerRef.current) {
        markerRef.current.remove();
      }

      // Add new marker
      markerRef.current = new mapboxgl.Marker()
        .setLngLat([location.lng, location.lat])
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(`<h3>${location.name || 'Location'}</h3>`))
        .addTo(map.current);
    };

    // If the map style is already loaded, update directly.
    // Otherwise, wait for the 'load' event (which fires once after all resources are loaded).
    if (map.current.isStyleLoaded()) {
      updateMapViewAndMarker();
    } else {
      const handleMapLoad = () => {
        if (map.current) { // Check if map still exists (it should, due to mount/unmount effect)
          updateMapViewAndMarker();
          map.current.off('load', handleMapLoad); // Important: remove listener after use
        }
      };
      map.current.on('load', handleMapLoad);
      
      // It's good practice to ensure this listener is cleaned up if the component unmounts
      // before 'load' fires, though map.remove() in the main cleanup *should* handle it.
      // For extra safety, this effect could return a cleanup for this specific listener,
      // but it adds complexity if map.current might be null then.
      // Relying on map.remove() from the mount effect is generally standard.
    }
    
  }, [location, zoom]); // Re-run when location or zoom props change

  // Render directions route when provided
  useEffect(() => {
    if (!map.current) return;

    const clearDirections = () => {
      try {
        if (map.current.getLayer(routeLayerIdRef.current)) {
          map.current.removeLayer(routeLayerIdRef.current);
        }
        if (map.current.getSource(routeSourceIdRef.current)) {
          map.current.removeSource(routeSourceIdRef.current);
        }
      } catch (_) {}
      if (originMarkerRef.current) { originMarkerRef.current.remove(); originMarkerRef.current = null; }
      if (destMarkerRef.current) { destMarkerRef.current.remove(); destMarkerRef.current = null; }
      if (typeof onDirectionsInfo === 'function') onDirectionsInfo(null);
    };

    if (!directions || !directions.origin || !directions.destination) {
      clearDirections();
      return;
    }

    const token = getMapboxToken();
    if (!token) return;

    const ensureStyleReady = (cb) => {
      if (map.current.isStyleLoaded()) cb();
      else {
        const onLoad = () => { cb(); map.current.off('load', onLoad); };
        map.current.on('load', onLoad);
      }
    };

    const fetchAndRender = async () => {
      try {
        const { origin, destination } = directions;
        const url = `https://api.mapbox.com/directions/v5/mapbox/${directionsProfile}/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?alternatives=false&geometries=geojson&overview=full&access_token=${token}`;
        const res = await fetch(url);
        const json = await res.json();
        const route = json?.routes?.[0];
        if (!route || !route.geometry) {
          clearDirections();
          return;
        }

        ensureStyleReady(() => {
          // Clear previous
          clearDirections();

          // Add source
          if (!map.current.getSource(routeSourceIdRef.current)) {
            map.current.addSource(routeSourceIdRef.current, {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: route.geometry
              }
            });
          }

          // Add layer
          if (!map.current.getLayer(routeLayerIdRef.current)) {
            map.current.addLayer({
              id: routeLayerIdRef.current,
              type: 'line',
              source: routeSourceIdRef.current,
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: { 'line-color': '#2b69f8', 'line-width': 5, 'line-opacity': 0.9 }
            });
          }

          // Markers for origin/destination
          originMarkerRef.current = new mapboxgl.Marker({ color: '#4CAF50' })
            .setLngLat([origin.lng, origin.lat])
            .addTo(map.current);
          destMarkerRef.current = new mapboxgl.Marker({ color: '#e53935' })
            .setLngLat([destination.lng, destination.lat])
            .addTo(map.current);

          // Fit bounds
          const coords = route.geometry.coordinates;
          let minX = coords[0][0], minY = coords[0][1], maxX = coords[0][0], maxY = coords[0][1];
          for (const c of coords) {
            if (c[0] < minX) minX = c[0];
            if (c[0] > maxX) maxX = c[0];
            if (c[1] < minY) minY = c[1];
            if (c[1] > maxY) maxY = c[1];
          }
          const bounds = [[minX, minY], [maxX, maxY]];
          try {
            map.current.fitBounds(bounds, {
              padding: {
                top: 60,
                right: 40,
                bottom: (lastPaddingRef.current?.bottom || 0) + 80,
                left: 40
              },
              duration: 500
            });
            lastRouteBoundsRef.current = bounds;
          } catch (_) {}
          if (typeof onDirectionsInfo === 'function') {
            onDirectionsInfo({
              distanceMeters: route.distance ?? 0,
              durationSeconds: route.duration ?? 0
            });
          }
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Directions fetch failed', e);
      }
    };

    fetchAndRender();

    return () => {
      clearDirections();
    };
  }, [directions, directionsProfile]);

  // Respond to bottom sheet height changes by adjusting map padding
  const handleSheetHeightChange = (h) => {
    if (!map.current) return;
    const nextPadding = { ...lastPaddingRef.current, bottom: h };
    try {
      if (typeof map.current.setPadding === 'function') {
        map.current.setPadding(nextPadding);
      } else {
        const center = map.current.getCenter();
        map.current.easeTo({ center, padding: nextPadding, duration: 0 });
      }
      lastPaddingRef.current = nextPadding;
    } catch (e) {
      // Fallback: ignore padding errors silently
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div 
        ref={mapContainer} 
        className={`map-display ${className}`} 
        data-testid="map-display"
        style={{ width: '100%', height: '100%' }}
      />
      {directions && lastRouteBoundsRef.current && (
        <button
          type="button"
          onClick={() => {
            try {
              map.current && map.current.fitBounds(lastRouteBoundsRef.current, {
                padding: {
                  top: 60,
                  right: 40,
                  bottom: (lastPaddingRef.current?.bottom || 0) + 80,
                  left: 40
                },
                duration: 400
              });
            } catch (_) {}
          }}
          style={{
            position: 'absolute',
            left: 12,
            top: 12,
            zIndex: 10,
            background: 'rgba(255,255,255,0.95)',
            border: '1px solid #e6eaf2',
            borderRadius: 8,
            padding: '6px 10px',
            cursor: 'pointer',
            fontSize: 12,
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}
        >
          Recenter
        </button>
      )}
      {bottomSheetEnabled && (
        <BottomDockedSheet
          isExpanded={bottomSheetExpanded}
          onToggle={onBottomSheetToggle}
          onCollapse={onBottomSheetCollapse}
          collapsedHeight={bottomSheetCollapsedHeight}
          expandedHeight={bottomSheetExpandedHeight}
          title={bottomSheetTitle}
          subtitle={bottomSheetSubtitle}
          onPrimary={onBottomSheetPrimary}
          primaryLabel={bottomSheetPrimaryLabel}
          onHeightChange={handleSheetHeightChange}
        >
          {bottomSheetContent}
        </BottomDockedSheet>
      )}
    </div>
  );
};

// New: Map with pins for each route
export const UserRoutesMap = ({ 
  routesWithPoints = [], 
  className = '', 
  zoom = 10, 
  fallbackCenter,
  showAllPoints = true,
  autoFitBounds = true,
  onRouteClick
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const sources = useRef([]);
  const layers = useRef([]);
  
  // State for toggle between routes with lines and points only
  const [showRouteLines, setShowRouteLines] = useState(true);
  
  // Temporarily remove useState to fix hot reload issue
  // const [hoveredRoute, setHoveredRoute] = useState(null);

  // Route colors for different routes
  const routeColors = [
    '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', 
    '#ffeaa7', '#dda0dd', '#98d8c8', '#f7b731'
  ];

  // Function to clear all map elements
  const clearMapElements = () => {
    if (!map.current) return;
    
    // Clear markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];
    
    // Clear layers and sources
    layers.current.forEach(layerId => {
      if (map.current.getLayer(layerId)) {
        map.current.removeLayer(layerId);
      }
    });
    sources.current.forEach(sourceId => {
      if (map.current.getSource(sourceId)) {
        map.current.removeSource(sourceId);
      }
    });
    layers.current = [];
    sources.current = [];
  };

  // Function to pre-calculate bounds from all route points
  const calculateInitialBounds = () => {
    console.log('[DEBUG] UserRoutesMap: Using simple US-centered view');
    
    /*
     * 🎯 SIMPLE US-CENTERED SOLUTION
     * 
     * PROBLEM: Complex zoom calculations and bounds fitting are causing
     * zoom-in/zoom-out animations and timing issues.
     * 
     * SOLUTION: Just center the map on the US with a wide zoom level
     * that will show all route pins clearly without any calculations
     * or bounds fitting operations.
     */
    
    // Simple US-centered view - no complex calculations
    const usCenter = [-98.5795, 39.8283]; // Center of the United States
    const wideZoom = 3; // Wide enough to show the entire United States from coast to coast
    
    console.log('[DEBUG] UserRoutesMap: Using US-centered view:', {
      center: usCenter,
      zoom: wideZoom
    });

    return {
      center: usCenter,
      zoom: wideZoom,
      hasValidBounds: true
    };
  };

  // Effect for map initialization and unmount cleanup
  useEffect(() => {
    const token = getMapboxToken();
    if (!token || !mapContainer.current) return;
    mapboxgl.accessToken = token;

    console.log('[DEBUG] UserRoutesMap: Initializing map with perfect zoom calculation');

    /*
     * 🎯 PERFECT ZOOM SOLUTION - NO BOUNDS FITTING
     * 
     * PROBLEM: All bounds fitting operations cause zoom-in/zoom-out animations
     * even with duration: 0, because they're correcting the initial view.
     * 
     * SOLUTION: Calculate the perfect zoom level from the start and
     * start the map at that exact zoom. No bounds fitting operations
     * means no corrections or animations - just one clean view.
     */
    
    // Pre-calculate perfect bounds from all route points
    const initialView = calculateInitialBounds();
    
    console.log('[DEBUG] UserRoutesMap: Creating map with perfect initial view:', initialView);

    // Create map with perfect initial view - no bounds fitting needed
    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: initialView.center,
      zoom: initialView.zoom,
      trackResize: true,
      // Add error handling options
      failIfMajorPerformanceCaveat: false,
      preserveDrawingBuffer: false,
      antialias: true
    });

    /*
     * 🛡️ ENHANCED ERROR HANDLING FOR MAPBOX
     * 
     * Added error event listeners to catch and log Mapbox errors.
     * This helps debug issues like "this.errorCb is not a function"
     * and other map initialization problems.
     */
    
    // Add error event listeners
    mapInstance.on('error', (e) => {
      console.error('[ERROR] Mapbox error:', e);
    });

    mapInstance.on('loaderror', (e) => {
      console.error('[ERROR] Mapbox load error:', e);
    });

    // Add navigation controls
    mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
    // Store map reference
    map.current = mapInstance;

    console.log('[DEBUG] UserRoutesMap: Map instance created with perfect initial view');

    // Wait for map to load before proceeding
    mapInstance.on('load', () => {
      console.log('[DEBUG] UserRoutesMap: Map loaded with perfect initial view');
      // Removed decorative boundary layers to avoid style compatibility issues
      // The routes will be rendered automatically when the effect runs
    });

    // Also listen for styledata event as backup
    mapInstance.on('styledata', () => {
      console.log('[DEBUG] UserRoutesMap: Map styledata event fired');
    });

    return () => {
      console.log('[DEBUG] UserRoutesMap: Cleaning up map');
      // Clean up markers
      markers.current.forEach(marker => marker.remove());
      markers.current = [];
      
      // Clean up sources and layers
      if (map.current) {
        layers.current.forEach(layerId => {
          if (map.current.getLayer(layerId)) {
            map.current.removeLayer(layerId);
          }
        });
        sources.current.forEach(sourceId => {
          if (map.current.getSource(sourceId)) {
            map.current.removeSource(sourceId);
          }
        });
        layers.current = [];
        sources.current = [];
        
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Effect to render routes when routesWithPoints changes
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded() || routesWithPoints.length === 0) {
      return;
    }

    console.log('[DEBUG] UserRoutesMap: Rendering routes, count:', routesWithPoints.length);

    // Clear existing markers and sources
    clearMapElements();

    const allRenderedCoords = []; // Track all coordinates that will be rendered

    // Render each route
    routesWithPoints.forEach((route, routeIndex) => {
      if (!route.points || route.points.length === 0) {
        console.log(`[DEBUG] UserRoutesMap: Route ${route.id} has no points, skipping`);
        return;
      }

      const routeColor = routeColors[routeIndex % routeColors.length];
      const routeCoords = route.points
        .filter(point => Array.isArray(point.coordinates))
        .map(point => point.coordinates);

      if (routeCoords.length === 0) {
        console.log(`[DEBUG] UserRoutesMap: Route ${route.id} has no valid coordinates, skipping`);
        return;
      }

      console.log(`[DEBUG] UserRoutesMap: Rendering route ${route.id} with ${routeCoords.length} points`);
      
      // Add coordinates to our tracking array
      allRenderedCoords.push(...routeCoords);

      // Add route line only if showRouteLines is true
      if (showRouteLines) {
        const lineId = `route-line-${route.id}`;
        const lineSourceId = `route-line-source-${route.id}`;

        if (!map.current.getSource(lineSourceId)) {
          try {
            map.current.addSource(lineSourceId, {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: { routeId: route.id, routeName: route.name },
                geometry: {
                  type: 'LineString',
                  coordinates: routeCoords
                }
              }
            });
            sources.current.push(lineSourceId);
          } catch (e) {
            console.error('[UserRoutesMap] addSource failed', lineSourceId, e);
            return; // Skip layer if source failed
          }

          try {
            map.current.addLayer({
              id: lineId,
              type: 'line',
              source: lineSourceId,
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': routeColor,
                'line-width': 4,
                'line-opacity': 0.8
              }
            });
            layers.current.push(lineId);
          } catch (e) {
            console.error('[UserRoutesMap] addLayer failed', lineId, e);
          }

          // Add hover effect
          map.current.on('mouseenter', lineId, () => {
            map.current.getCanvas().style.cursor = 'pointer';
            // setHoveredRoute(route); // Temporarily removed to fix hot reload
          });

          map.current.on('mouseleave', lineId, () => {
            map.current.getCanvas().style.cursor = '';
            // setHoveredRoute(null); // Temporarily removed to fix hot reload
          });

          map.current.on('click', lineId, () => {
            if (onRouteClick) {
              onRouteClick(route);
            }
          });
        }
      }

      // Add markers for each point
      route.points.forEach((point, pointIndex) => {
        if (!Array.isArray(point.coordinates)) return;

        const markerId = `marker-${route.id}-${pointIndex}`;
        const marker = new mapboxgl.Marker({
          color: routeColor,
          scale: 0.8
        })
          .setLngLat(point.coordinates)
          .setPopup(
            new mapboxgl.Popup({ offset: 25 })
              .setHTML(`
                <div style="font-family: Arial, sans-serif; padding: 8px;">
                  <strong>${route.name}</strong><br>
                  Point ${pointIndex + 1}<br>
                  ${point.name || 'Unnamed location'}
                </div>
              `)
          )
          .addTo(map.current);

        markers.current.push(marker);
      });
    });

    console.log('[DEBUG] UserRoutesMap: All routes rendered successfully');
    console.log('[DEBUG] UserRoutesMap: Total coordinates rendered:', allRenderedCoords.length);
    
    /*
     * 🎯 NO BOUNDS FITTING - PERFECT INITIAL VIEW
     * 
     * PROBLEM: All bounds fitting operations cause zoom-in/zoom-out animations
     * even with duration: 0, because they're correcting the initial view.
     * 
     * SOLUTION: The map already starts at the perfect zoom level calculated
     * from all route points. No bounds fitting needed - just render the routes
     * and they'll appear in the correct view immediately.
     */
    
    console.log('[DEBUG] UserRoutesMap: Routes rendered at perfect initial zoom level');
    
      }, [routesWithPoints, onRouteClick, showRouteLines]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '500px', borderRadius: '16px', margin: '0 auto' }}>
      <FullscreenMapButton mapContainerRef={mapContainer} />
      
      {/* Toggle Button for Routes vs Points */}
      {routesWithPoints.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          <button
            onClick={() => setShowRouteLines(!showRouteLines)}
            style={{
              background: showRouteLines ? '#4ecdc4' : '#ff6b6b',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            title={showRouteLines ? 'Switch to Points Only' : 'Show Route Lines'}
          >
            {showRouteLines ? '📍 Points Only' : '🛣️ Show Routes'}
          </button>
        </div>
      )}
      
    <div
      ref={mapContainer}
      className={`map-display user-routes-map ${className}`}
        style={{ width: '100%', height: '100%', borderRadius: '16px' }}
      data-testid="user-routes-map"
    />
    </div>
  );
};

// New: Map component for user profile that shows both routes and posts
export const UserProfileMap = ({ 
  routesWithPoints = [], 
  posts = [],
  className = '', 
  zoom = 10, 
  fallbackCenter,
  showAllPoints = true,
  autoFitBounds = true,
  onRouteClick,
  onPostClick
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const sources = useRef([]);
  const layers = useRef([]);
  
  // State for toggle between routes with lines and points only
  const [showRouteLines, setShowRouteLines] = useState(true);
  const [showPosts, setShowPosts] = useState(true);
  const [showRoutes, setShowRoutes] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Route colors for different routes
  const routeColors = [
    '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', 
    '#ffeaa7', '#dda0dd', '#98d8c8', '#f7b731'
  ];

  // Post marker color
  const postMarkerColor = '#4CAF50'; // Green for posts

  // Function to clear all map elements
  const clearMapElements = () => {
    if (!map.current) return;
    
    // Clear markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];
    
    // Clear layers and sources
    layers.current.forEach(layerId => {
      try {
        if (map.current.getLayer(layerId)) {
          map.current.removeLayer(layerId);
        }
      } catch (err) {
        console.error('[Mapbox] Error removing layer', layerId, err);
      }
    });
    sources.current.forEach(sourceId => {
      try {
        if (map.current.getSource(sourceId)) {
          map.current.removeSource(sourceId);
        }
      } catch (err) {
        console.error('[Mapbox] Error removing source', sourceId, err);
      }
    });
    layers.current = [];
    sources.current = [];
  };

  // Function to pre-calculate bounds from all route points and posts
  const calculateInitialBounds = () => {
    console.log('[DEBUG] UserProfileMap: Calculating bounds from routes and posts');
    
    const allCoordinates = [];
    
    // Add route coordinates
    routesWithPoints.forEach(route => {
      if (route.points && route.points.length > 0) {
        route.points.forEach(point => {
          if (Array.isArray(point.coordinates)) {
            allCoordinates.push(point.coordinates);
          }
        });
      }
    });
    
    // Add post coordinates
    posts.forEach(post => {
      if (post.place && typeof post.place.lat === 'number' && typeof post.place.lng === 'number') {
        allCoordinates.push([post.place.lng, post.place.lat]);
      }
    });
    
    console.log('[DEBUG] UserProfileMap: Found coordinates:', {
      routeCoordinates: routesWithPoints.reduce((sum, r) => sum + (r.points?.length || 0), 0),
      postCoordinates: posts.filter(p => p.place && typeof p.place.lat === 'number' && typeof p.place.lng === 'number').length,
      totalCoordinates: allCoordinates.length
    });
    
    if (allCoordinates.length === 0) {
      console.log('[DEBUG] UserProfileMap: No coordinates found, using US-centered view');
      return {
        center: [-98.5795, 39.8283], // Center of the United States
        zoom: 3,
        hasValidBounds: false
      };
    }
    
    // Calculate bounds
    const lngs = allCoordinates.map(coord => coord[0]);
    const lats = allCoordinates.map(coord => coord[1]);
    
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    
    const center = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
    
    // Calculate zoom level based on bounds
    const lngDiff = maxLng - minLng;
    const latDiff = maxLat - minLat;
    const maxDiff = Math.max(lngDiff, latDiff);
    
    let zoom = 10;
    if (maxDiff > 10) zoom = 3;
    else if (maxDiff > 5) zoom = 5;
    else if (maxDiff > 2) zoom = 7;
    else if (maxDiff > 1) zoom = 9;
    else if (maxDiff > 0.5) zoom = 11;
    else if (maxDiff > 0.1) zoom = 13;
    else zoom = 15;
    
    console.log('[DEBUG] UserProfileMap: Calculated bounds:', {
      center,
      zoom,
      coordinateCount: allCoordinates.length,
      bounds: { minLng, maxLng, minLat, maxLat }
    });

    return {
      center,
      zoom,
      hasValidBounds: true
    };
  };

  // Effect for map initialization and unmount cleanup
  useEffect(() => {
    console.log('[DEBUG] UserProfileMap: Map initialization effect triggered');
    console.log('[DEBUG] UserProfileMap: Initial state:', {
      isMapReady,
      dataLoaded,
      routesCount: routesWithPoints.length,
      postsCount: posts.length,
      showRoutes,
      showPosts,
      showRouteLines
    });
    
    const token = getMapboxToken();
    if (!token || !mapContainer.current) {
      console.log('[DEBUG] UserProfileMap: Missing token or container, returning');
      return;
    }
    mapboxgl.accessToken = token;

    console.log('[DEBUG] UserProfileMap: Initializing map');

    // Pre-calculate bounds from all data
    const initialView = calculateInitialBounds();
    
    console.log('[DEBUG] UserProfileMap: Creating map with initial view:', initialView);

    // Create map with calculated initial view
    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: initialView.center,
      zoom: initialView.zoom,
      trackResize: true,
      failIfMajorPerformanceCaveat: false,
      preserveDrawingBuffer: false,
      antialias: true
    });

    // Add error event listeners
    mapInstance.on('error', (e) => {
      console.error('[ERROR] Mapbox error:', e);
    });

    mapInstance.on('loaderror', (e) => {
      console.error('[ERROR] Mapbox load error:', e);
    });

    // Add navigation controls
    mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
    // Store map reference
    map.current = mapInstance;

    console.log('[DEBUG] UserProfileMap: Map instance created');

    // Wait for map to load before proceeding
    mapInstance.on('load', () => {
      console.log('[DEBUG] UserProfileMap: Map loaded event fired');
      console.log('[DEBUG] UserProfileMap: Setting isMapReady=true and dataLoaded=true');
      setIsMapReady(true);
      setDataLoaded(true); // Mark data as loaded immediately when map is ready
      
      // Decorative boundary layers removed to prevent style compatibility errors
    });

    return () => {
      console.log('[DEBUG] UserProfileMap: Cleaning up map');
      // Clean up markers
      markers.current.forEach(marker => marker.remove());
      markers.current = [];
      
      // Clean up sources and layers
      if (map.current) {
        layers.current.forEach(layerId => {
          if (map.current.getLayer(layerId)) {
            map.current.removeLayer(layerId);
          }
        });
        sources.current.forEach(sourceId => {
          if (map.current.getSource(sourceId)) {
            map.current.removeSource(sourceId);
          }
        });
        layers.current = [];
        sources.current = [];
        
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Effect to render routes and posts when data changes
  useEffect(() => {
    console.log('[DEBUG] UserProfileMap: Data rendering effect triggered');
    console.log('[DEBUG] UserProfileMap: Current state:', {
      hasMap: !!map.current,
      isMapReady,
      dataLoaded,
      routesCount: routesWithPoints.length,
      postsCount: posts.length,
      showRoutes,
      showPosts,
      showRouteLines,
      isStyleLoaded: map.current ? map.current.isStyleLoaded() : 'N/A'
    });
    
    if (!map.current || !isMapReady) {
      console.log('[DEBUG] UserProfileMap: Map not ready yet, returning early', { 
        hasMap: !!map.current, 
        isMapReady 
      });
      return;
    }

    // The 'load' event already guarantees the map style is loaded
    // No need to check isStyleLoaded() as it can be false even after load event
    console.log('[DEBUG] UserProfileMap: Map is ready, proceeding with data rendering');

    console.log('[DEBUG] UserProfileMap: ✅ All checks passed, proceeding to render data');
    console.log('[DEBUG] UserProfileMap: Rendering data', {
      routesCount: routesWithPoints.length,
      postsCount: posts.length,
      showRoutes,
      showPosts,
      showRouteLines
    });
    
    console.log('[DEBUG] UserProfileMap: Routes data:', routesWithPoints);
    console.log('[DEBUG] UserProfileMap: Posts data:', posts);

    // Clear existing markers and sources
    clearMapElements();

    const allRenderedCoords = []; // Track all coordinates that will be rendered

    // Render routes if enabled
    if (showRoutes) {
      routesWithPoints.forEach((route, routeIndex) => {
        if (!route.points || route.points.length === 0) {
          console.log(`[DEBUG] UserProfileMap: Route ${route.id} has no points, skipping`);
          return;
        }

        const routeColor = routeColors[routeIndex % routeColors.length];
        const routeCoords = route.points
          .filter(point => Array.isArray(point.coordinates))
          .map(point => point.coordinates);

        if (routeCoords.length === 0) {
          console.log(`[DEBUG] UserProfileMap: Route ${route.id} has no valid coordinates, skipping`);
          return;
        }

        console.log(`[DEBUG] UserProfileMap: Rendering route ${route.id} with ${routeCoords.length} points`);
        
        // Add coordinates to our tracking array
        allRenderedCoords.push(...routeCoords);

        // Add route line only if showRouteLines is true
        if (showRouteLines) {
          const lineId = `route-line-${route.id}`;
          const lineSourceId = `route-line-source-${route.id}`;

          try {
            if (!map.current.getSource(lineSourceId)) {
              map.current.addSource(lineSourceId, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  properties: { routeId: route.id, routeName: route.name },
                  geometry: {
                    type: 'LineString',
                    coordinates: routeCoords
                  }
                }
              });
              sources.current.push(lineSourceId);
            }
          } catch (err) {
            console.error('[Mapbox] Error adding source', lineSourceId, err);
          }

          try {
            if (!map.current.getLayer(lineId)) {
              map.current.addLayer({
                id: lineId,
                type: 'line',
                source: lineSourceId,
                layout: {
                  'line-join': 'round',
                  'line-cap': 'round'
                },
                paint: {
                  'line-color': routeColor,
                  'line-width': 4,
                  'line-opacity': 0.8
                }
              });
              layers.current.push(lineId);

              // Add hover effect
              map.current.on('mouseenter', lineId, () => {
                map.current.getCanvas().style.cursor = 'pointer';
              });

              map.current.on('mouseleave', lineId, () => {
                map.current.getCanvas().style.cursor = '';
              });

              map.current.on('click', lineId, () => {
                if (onRouteClick) {
                  onRouteClick(route);
                }
              });
            }
          } catch (err) {
            console.error('[Mapbox] Error adding layer', lineId, err);
          }
        }

        // Add markers for each route point
        route.points.forEach((point, pointIndex) => {
          if (!Array.isArray(point.coordinates)) return;

          const markerId = `route-marker-${route.id}-${pointIndex}`;
          const marker = new mapboxgl.Marker({
            color: routeColor,
            scale: 0.8
          })
            .setLngLat(point.coordinates)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 })
                .setHTML(`
                  <div style="font-family: Arial, sans-serif; padding: 8px;">
                    <strong>${route.name}</strong><br>
                    Point ${pointIndex + 1}<br>
                    ${point.name || 'Unnamed location'}
                  </div>
                `)
            )
            .addTo(map.current);

          markers.current.push(marker);
        });
      });
    }

    // Render posts if enabled
    if (showPosts) {
      posts.forEach((post, postIndex) => {
        if (!post.place || typeof post.place.lat !== 'number' || typeof post.place.lng !== 'number') {
          console.log(`[DEBUG] UserProfileMap: Post ${post.id} has no valid location, skipping`);
          return;
        }

        const coordinates = [post.place.lng, post.place.lat];
        allRenderedCoords.push(coordinates);

        console.log(`[DEBUG] UserProfileMap: Rendering post ${post.id} at coordinates:`, coordinates);

        const markerId = `post-marker-${post.id}`;
        const marker = new mapboxgl.Marker({
          color: postMarkerColor,
          scale: 1.0
        })
          .setLngLat(coordinates)
          .setPopup(
            new mapboxgl.Popup({ offset: 25 })
              .setHTML(`
                <div style="font-family: Arial, sans-serif; padding: 8px;">
                  <strong>${post.title || 'Post'}</strong><br>
                  ${post.place.name || 'Location'}<br>
                  ${post.content ? post.content.substring(0, 100) + (post.content.length > 100 ? '...' : '') : ''}
                </div>
              `)
          )
          .addTo(map.current);

        // Add click handler for posts
        marker.getElement().addEventListener('click', () => {
          if (onPostClick) {
            onPostClick(post);
          }
        });

        markers.current.push(marker);
      });
    }

    console.log('[DEBUG] UserProfileMap: All data rendered successfully');
    console.log('[DEBUG] UserProfileMap: Total coordinates rendered:', allRenderedCoords.length);
    
  }, [routesWithPoints, posts, onRouteClick, onPostClick, showRouteLines, showRoutes, showPosts, isMapReady]);

  // Debug effect: Log when any dependency changes
  useEffect(() => {
    console.log('[DEBUG] UserProfileMap: Dependencies changed:', {
      routesWithPointsLength: routesWithPoints.length,
      postsLength: posts.length,
      showRoutes,
      showPosts,
      showRouteLines,
      isMapReady,
      dataLoaded
    });
  }, [routesWithPoints, posts, showRoutes, showPosts, showRouteLines, isMapReady, dataLoaded]);

  return (
    <>
      <style>
        {`
          @keyframes fadeOut {
            0% { opacity: 1; }
            70% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}
      </style>
      <div style={{ position: 'relative', width: '100%', height: '500px', borderRadius: '16px', margin: '0 auto' }}>
      <FullscreenMapButton mapContainerRef={mapContainer} />
      
      {/* Toggle Buttons */}
      <div style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        {/* Routes toggle - always show */}
        <button
          onClick={() => setShowRoutes(!showRoutes)}
          style={{
            background: showRoutes ? '#4ecdc4' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 12px',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            opacity: routesWithPoints.length > 0 ? 1 : 0.6
          }}
          title={showRoutes ? 'Hide Routes' : 'Show Routes'}
          disabled={routesWithPoints.length === 0}
                  >
            {showRoutes ? '🛣️ Hide Routes' : '🛣️ Show Routes'}
            {routesWithPoints.length === 0 && ' (0)'}
            {showRoutes && routesWithPoints.length > 0 && ' ✅'}
          </button>
        
        {/* Posts toggle - always show */}
        <button
          onClick={() => setShowPosts(!showPosts)}
          style={{
            background: showPosts ? '#4CAF50' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 12px',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            opacity: posts.length > 0 ? 1 : 0.6
          }}
          title={showPosts ? 'Hide Posts' : 'Show Posts'}
          disabled={posts.length === 0}
                  >
            {showPosts ? '📝 Hide Posts' : '📝 Show Posts'}
            {posts.length === 0 && ' (0)'}
            {showPosts && posts.length > 0 && ' ✅'}
          </button>
        
        {/* Route lines toggle - always show when routes are enabled */}
        <button
          onClick={() => setShowRouteLines(!showRouteLines)}
          style={{
            background: showRouteLines ? '#45b7d1' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 12px',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            opacity: (routesWithPoints.length > 0 && showRoutes) ? 1 : 0.6
          }}
          title={showRouteLines ? 'Hide Route Lines' : 'Show Route Lines'}
          disabled={routesWithPoints.length === 0 || !showRoutes}
                  >
            {showRouteLines ? '📍 Points Only' : '🛣️ Show Lines'}
            {routesWithPoints.length === 0 && ' (0)'}
            {showRouteLines && routesWithPoints.length > 0 && showRoutes && ' ✅'}
          </button>
      </div>
      
      {/* Loading indicator */}
      {!isMapReady && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          zIndex: 1000
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗺️</div>
          <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>Loading Map...</h3>
          <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
            Preparing your locations
          </p>
          <p style={{ margin: '8px 0 0 0', color: '#999', fontSize: '12px' }}>
            Routes: {routesWithPoints.length} | Posts: {posts.length}
          </p>
        </div>
      )}



      {/* Status indicator - always show current state */}
      {isMapReady && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: 'rgba(255, 255, 255, 0.95)',
          color: '#333',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 'bold',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🛣️ Routes: {showRoutes ? 'ON' : 'OFF'}</span>
            <span>📝 Posts: {showPosts ? 'ON' : 'OFF'}</span>
            {routesWithPoints.length > 0 && showRoutes && (
              <span>📍 Lines: {showRouteLines ? 'ON' : 'OFF'}</span>
            )}
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
            {routesWithPoints.length} routes, {posts.length} posts available
          </div>
        </div>
      )}

      {/* No data message */}
      {isMapReady && routesWithPoints.length === 0 && posts.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          zIndex: 1000
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗺️</div>
          <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>No Locations Yet</h3>
          <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
            Create posts with locations or routes to see them on the map!
          </p>
        </div>
      )}
      
      <div
        ref={mapContainer}
        className={`map-display user-profile-map ${className}`}
        style={{ width: '100%', height: '100%', borderRadius: '16px' }}
        data-testid="user-profile-map"
      />
      </div>
    </>
  );
};

export default MapDisplay; 
